import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react';

/**
 * WHO GETS THIS, WHO DOESN'T, AND HOW IT GOES OUT — read from the send's own resolver (the preview
 * calls it), so every number here is what the send will do, not a second estimate.
 *
 * It answered one question ("audience size: 1,234") and left the three an operator asks before
 * messaging a past event's crowd: who is left out and why — above all, the people who told us to stop,
 * in their own words, so nobody wonders whether they were respected — and how many clicks it takes,
 * because a batch is a deliberate checkpoint (look at the replies, then send the next).
 */
export const AudienceBreakdown = ({ resolved, channel, includeRecent = false, onIncludeRecent }) => {
  const [showStopped, setShowStopped] = useState(false);
  const d = resolved.detail;
  const reach = channel === 'whatsapp' ? 'on WhatsApp' : channel === 'sms' ? 'by SMS' : 'by email';
  const address = channel === 'email' ? 'email address' : 'phone number';

  if (resolved.status === 'loading') {
    return <div className="bg-white border border-[#E6E4E1] rounded-md px-4 py-3 text-[12px] text-[#6B7280] inline-flex items-center gap-1.5 w-full"><Loader2 className="w-3.5 h-3.5 animate-spin" />Working out who this reaches…</div>;
  }
  if (resolved.status === 'error') {
    return <div className="bg-white border border-[#F3C9C9] rounded-md px-4 py-3 text-[12px] text-[#9B1F1F]">{resolved.error || 'Could not work out the audience'}</div>;
  }
  if (resolved.status !== 'ready' || !d?.excluded) return null;

  const x = d.excluded;
  const stopped = x.optedOutCount ?? x.optedOut?.length ?? 0;
  const left = [
    stopped ? { k: 'stop', n: stopped, label: channel === 'email' ? 'unsubscribed from these emails' : 'asked us to stop messaging them', strong: true } : null,
    x.noAddress ? { k: 'noaddr', n: x.noAddress, label: `have no ${address} we can use` } : null,
    x.duplicates ? { k: 'dup', n: x.duplicates, label: 'more orders or sign-ups by someone already counted — each person gets it once' } : null,
    // Email reads its own blast log (this same message); WhatsApp reads the message ledger (another campaign).
    x.recentlySent ? { k: 'recent', n: x.recentlySent, label: channel === 'whatsapp' ? `heard from another campaign in the last ${x.cooldownDays || 7} days` : `got this same message in the last ${x.cooldownDays || 7} days` } : null,
  ].filter(Boolean);
  const total = resolved.total || 0;
  const batch = d.batchSize || total;
  const batches = d.batches || 1;

  return (
    <div className="bg-white border border-[#1F2937] rounded-md">
      <div className="px-4 py-3 flex items-baseline justify-between gap-3">
        <div>
          <div className="text-[18px] font-semibold tabular-nums text-[#1F2937]">{total.toLocaleString()} <span className="text-[13px] font-medium">{total === 1 ? 'person' : 'people'}</span></div>
          <div className="text-[11px] text-[#6B7280]">will get this {reach}</div>
        </div>
        {x.capped && <div className="text-[10.5px] text-[#8A5A12] max-w-[240px] text-right">Only the first 50,000 of this segment are read.</div>}
      </div>

      {(left.length > 0 || includeRecent) && (
        <div className="border-t border-[#EEEDEA] px-4 py-2.5">
          <div className="text-[10.5px] uppercase tracking-[0.06em] font-semibold text-[#6B7280] mb-1.5">Left out</div>
          {left.length === 0 && <div className="text-[12px] text-[#6B7280]">Nobody.</div>}
          <ul className="space-y-1">
            {left.map((r) => (
              <li key={r.k} className="text-[12px] text-[#374151]">
                {r.k === 'stop' ? (
                  <button onClick={() => setShowStopped((v) => !v)} className="inline-flex items-center gap-1 text-left hover:underline">
                    {showStopped ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                    <span className="tabular-nums font-semibold text-[#9B1F1F]">{r.n.toLocaleString()}</span>
                    <span>{r.n === 1 ? 'person' : 'people'} {r.label}</span>
                  </button>
                ) : (
                  <span className="pl-4 inline-block"><span className="tabular-nums font-medium text-[#1F2937]">{r.n.toLocaleString()}</span> {r.label}</span>
                )}
                {r.k === 'recent' && (x.recentCampaigns || []).length > 0 && (
                  <ul className="pl-8 mt-0.5 space-y-0.5 text-[11px] text-[#6B7280]">
                    {x.recentCampaigns.map((g) => (
                      <li key={g.campaignId}>{g.people.toLocaleString()} from “{g.name}”, last on {new Date(g.at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}</li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
          {onIncludeRecent && (x.recentlySent > 0 || includeRecent) && channel === 'whatsapp' && (
            <label className="mt-2 flex items-center gap-1.5 text-[11.5px] text-[#4B5563] cursor-pointer">
              <input type="checkbox" checked={includeRecent} onChange={(e) => onIncludeRecent(e.target.checked)} />
              Include people another campaign reached this week anyway
            </label>
          )}
          {showStopped && stopped > 0 && (
            <div className="mt-2 max-h-[220px] overflow-y-auto border border-[#EEEDEA] rounded">
              <table className="w-full text-[11.5px]">
                <tbody>
                  {(x.optedOut || []).map((p) => (
                    <tr key={p.phone || p.email} className="border-t border-[#F2F1ED] first:border-t-0 align-top">
                      <td className="px-2.5 py-1.5 text-[#1F2937] whitespace-nowrap">{p.name || '—'}<div className="text-[10.5px] text-[#9CA3AF] font-mono">{p.phone || p.email}</div></td>
                      <td className="px-2.5 py-1.5 text-[#4B5563]">{p.said ? <>“{p.said}”</> : <span className="text-[#9CA3AF]">{p.email ? 'by an email’s unsubscribe link' : 'no words recorded'}</span>}</td>
                      <td className="px-2.5 py-1.5 text-[#9CA3AF] whitespace-nowrap tabular-nums">{p.at ? new Date(p.at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {stopped > (x.optedOut || []).length && <div className="px-2.5 py-1.5 text-[10.5px] text-[#9CA3AF] border-t border-[#F2F1ED]">Showing {(x.optedOut || []).length} of {stopped.toLocaleString()}.</div>}
            </div>
          )}
        </div>
      )}

      {total > 0 && (
        <div className="border-t border-[#EEEDEA] px-4 py-2.5 text-[11.5px] text-[#4B5563]">
          {batches <= 1
            ? <>All {total.toLocaleString()} go out in one batch.</>
            : <>Sent in batches of <b className="tabular-nums text-[#1F2937]">{batch.toLocaleString()}</b> — <b>{batches}</b> batches. The first goes when you press <b>Send now</b>; each next one is a click on the campaign, after you have seen how the last one landed.</>}
        </div>
      )}
    </div>
  );
};
